import React, {useContext, useEffect} from "react"
import fetchPostBySlug from "../../fetch/FetchPostBySlug"
import PropTypes from "prop-types"
import parse from "html-react-parser"
import NavBar from "../../components/navigation/NavBar"
import Footer from "../../components/footer/Footer"
import MbpHead from "../../components/head/MbpHead"
import {useRouter} from "next/router"
import DataContext from "../../context/DataContext"
import {serverSideTranslations} from "next-i18next/serverSideTranslations"
import nextI18NextConfig from "../../next-i18next.config"
import BlogPostContent from "../../components/Blog/BlogPostContent"
import {getPageData} from "../../pageData"
import fetchPostSlugs from "../../fetch/FetchPostSlugs"

type Props = {
    post: any
}

const Post: React.FC<Props> = ({post}) => {
	const router = useRouter()
	const {setData} = useContext(DataContext)

	useEffect(() => {
		setData(post)
		return () => {
			setData(null)
		}
	}, [router.locale])

	return (
		post ? <>
			<MbpHead>
				<title>{`${parse(post.title)} | MyBikePlan`}</title>
				{parse(post.yoast_head)}
			</MbpHead>
			<NavBar/>
			<BlogPostContent title={post.title}
				content={post.content}/>
			<Footer/>
		</> : null
	)
}

Post.propTypes = {
	post: PropTypes.any.isRequired
}

export async function getStaticPaths() {
	// Call an external API endpoint to get posts
	const postsDe = await fetchPostSlugs(false, "de")
	const postsFr = await fetchPostSlugs(false, "fr")

	// Get the paths we want to pre-render based on posts
	const pathsDe = postsDe.map(slug => ({
		params: {slug: slug.trim()}, locale: "de"
	}))

	const pathsFr = postsFr.map(slug => ({
		params: {slug: slug.trim()}, locale: "fr"
	}))

	const paths = pathsDe.concat(pathsFr)

	// We'll pre-render only these paths at build time.
	// { fallback: false } means other routes should 404.
	return {paths, fallback: "blocking"}
}

export async function getStaticProps({locale, params}) {
	const post = await fetchPostBySlug(params.slug)
	const pageData = await getPageData(locale, [])

	const updatedPageData = Object.fromEntries(
		Object.entries(pageData).map(([key, value]) => [key, value === undefined ? null : value])
	)

	return {
		props: {
			post: post || null,
			...updatedPageData,
			...(await serverSideTranslations(locale, ["common", "URL", "search", "filters"], nextI18NextConfig))
		}
	}
}

export default Post
